import React from 'react';

export type ChartNode = {
  id: string;
  label: string;
  detail: string;
  style: string;
  parent?: string;
  tier?: number;
  edge?: 'solid' | 'dashed';
};

export type CapitalFlow = {
  id: string;
  label: string;
  steps: string[];
};

export type TimelineStep = {
  id: string;
  label: string;
  detail: string;
};

export type RestructuringChart = {
  id: string;
  title: string;
  caption: string;
  layout: 'tree' | 'flows' | 'timeline';
  nodes?: ChartNode[];
  flows?: CapitalFlow[];
  steps?: TimelineStep[];
};

const nodeTone: Record<string, string> = {
  person: 'border-ink-900 bg-ink-900 text-mist-50',
  partners: 'border-ink-700 bg-ink-800 text-mist-50',
  holdco: 'border-navy-700 bg-navy-900 text-mist-50',
  opco: 'border-navy-400 bg-white text-ink-900',
  gp: 'border-stone-400 bg-mist-100 text-ink-900',
  fund: 'border-mist-200 bg-white text-ink-900',
  independent: 'border-dashed border-stone-400 bg-mist-50 text-ink-800',
  default: 'border-mist-200 bg-white text-ink-900',
};

function NodeCard({
  node,
  delayMs = 0,
  className = '',
}: {
  node: ChartNode;
  delayMs?: number;
  className?: string;
}) {
  const dashed = node.edge === 'dashed';
  const dark =
    node.style === 'person' ||
    node.style === 'partners' ||
    node.style === 'holdco';
  return (
    <div
      className={`border px-3 py-2.5 text-center shadow-sm transition duration-500 animate-fade-up ${
        nodeTone[node.style] || nodeTone.default
      } ${dashed ? 'border-dashed' : ''} ${className}`}
      style={{ animationDelay: `${delayMs}ms` }}
    >
      <p className="text-[11px] font-semibold leading-snug tracking-wide">
        {node.label}
      </p>
      {node.detail && (
        <p
          className={`mt-1 text-[10px] leading-snug ${
            dark ? 'text-mist-200/90' : 'text-ink-700'
          }`}
        >
          {node.detail}
        </p>
      )}
    </div>
  );
}

function ConnectorDown({ dashed = false }: { dashed?: boolean }) {
  return (
    <div className="flex justify-center py-2" aria-hidden>
      <span
        className={`h-5 w-px ${dashed ? 'bg-stone-300' : 'bg-mist-200'}`}
      />
    </div>
  );
}

/** Ownership tiers. Independent-style nodes on a mixed tier sit askew to the side. */
function TieredOrgChart({ nodes }: { nodes: ChartNode[] }) {
  const tiered = nodes.filter((n) => typeof n.tier === 'number');
  const tiers = Array.from(
    new Set(tiered.map((n) => n.tier as number)),
  ).sort((a, b) => a - b);

  if (!tiers.length) {
    return <LegacyTreeChart nodes={nodes} />;
  }

  return (
    <div className="overflow-x-auto pb-2">
      <div className="mx-auto min-w-[56rem] max-w-6xl">
        {tiers.map((tier, idx) => {
          const row = nodes
            .filter((n) => n.tier === tier)
            .sort((a, b) => nodes.indexOf(a) - nodes.indexOf(b));
          const trunk = row.filter((n) => n.style !== 'independent');
          const aside = row.filter((n) => n.style === 'independent');
          const split = trunk.length > 0 && aside.length > 0;
          const cardWidth =
            tier === 1
              ? 'min-w-[12.5rem] max-w-[16rem]'
              : 'min-w-[10rem] max-w-[13rem]';

          if (!split) {
            return (
              <div key={tier}>
                {idx > 0 && <ConnectorDown />}
                <div className="flex flex-wrap justify-center gap-4">
                  {row.map((node) => (
                    <NodeCard
                      key={node.id}
                      node={node}
                      delayMs={tier * 90}
                      className={cardWidth}
                    />
                  ))}
                </div>
              </div>
            );
          }

          return (
            <div key={tier} className="relative">
              {idx > 0 && (
                <div className="grid grid-cols-[1fr_minmax(14rem,18rem)_1fr] items-start">
                  <div />
                  <ConnectorDown />
                  <div />
                </div>
              )}
              <div className="grid grid-cols-[1fr_minmax(14rem,18rem)_1fr] items-start gap-x-4">
                <div aria-hidden />
                <div className="flex flex-col items-center gap-3">
                  {trunk.map((node) => (
                    <NodeCard
                      key={node.id}
                      node={node}
                      delayMs={tier * 90}
                      className="w-full"
                    />
                  ))}
                </div>
                <div className="flex justify-start pl-2 md:pl-6">
                  <div className="mt-0 max-w-[20rem]">
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-500">
                      Independent
                    </p>
                    <div className="flex flex-col gap-3 border-l border-dashed border-stone-300 pl-4">
                      {aside.map((node, i) => (
                        <NodeCard
                          key={node.id}
                          node={node}
                          delayMs={tier * 90 + (i + 1) * 60}
                          className={cardWidth}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LegacyTreeChart({ nodes }: { nodes: ChartNode[] }) {
  const byParent = new Map<string | undefined, ChartNode[]>();
  for (const node of nodes) {
    const key = node.parent;
    const list = byParent.get(key) || [];
    list.push(node);
    byParent.set(key, list);
  }

  function renderChildren(parentId: string, depth: number): React.ReactNode {
    const children = byParent.get(parentId) || [];
    if (!children.length) return null;
    const multi = children.length > 1;
    return (
      <div className="flex w-full flex-col items-center pt-1">
        <ConnectorDown dashed={children.some((c) => c.edge === 'dashed')} />
        {multi && (
          <div className="relative mb-2 h-px w-[calc(100%-5rem)] max-w-3xl bg-mist-200" aria-hidden />
        )}
        <ul className="flex flex-wrap justify-center gap-x-5 gap-y-6">
          {children.map((node) => (
            <li
              key={node.id}
              className="relative flex min-w-[10rem] max-w-[14rem] flex-col items-center"
            >
              {multi && (
                <span
                  className={`absolute -top-2 left-1/2 h-2 w-px -translate-x-1/2 ${
                    node.edge === 'dashed' ? 'bg-stone-300' : 'bg-mist-200'
                  }`}
                  aria-hidden
                />
              )}
              <NodeCard
                node={node}
                delayMs={depth * 80}
                className={
                  node.style === 'gp'
                    ? 'w-full min-w-[14rem] max-w-[18rem]'
                    : 'w-full'
                }
              />
              {renderChildren(node.id, depth + 1)}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const roots = byParent.get(undefined) || [];
  if (!roots.length) {
    return <p className="text-sm text-ink-700">No chart nodes.</p>;
  }

  return (
    <div className="overflow-x-auto pb-2">
      <div className="mx-auto flex min-w-[36rem] max-w-4xl flex-col items-center">
        {roots.map((root) => (
          <div key={root.id} className="flex w-full flex-col items-center">
            <NodeCard
              node={root}
              className={
                root.style === 'gp'
                  ? 'min-w-[16rem] max-w-[20rem]'
                  : 'min-w-[12rem] max-w-[16rem]'
              }
            />
            {renderChildren(root.id, 1)}
          </div>
        ))}
      </div>
    </div>
  );
}

function FlowsChart({ flows }: { flows: CapitalFlow[] }) {
  return (
    <ul className="space-y-5">
      {flows.map((flow, i) => (
        <li
          key={flow.id}
          className="animate-fade-up"
          style={{ animationDelay: `${i * 70}ms` }}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-navy-700">
            {flow.label}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {flow.steps.map((step, idx) => (
              <React.Fragment key={`${flow.id}-${idx}`}>
                {idx > 0 && (
                  <span className="text-stone-400" aria-hidden>
                    →
                  </span>
                )}
                <span className="border border-mist-200 bg-white px-3 py-1.5 text-sm text-ink-900">
                  {step}
                </span>
              </React.Fragment>
            ))}
          </div>
        </li>
      ))}
    </ul>
  );
}

function TimelineChart({ steps }: { steps: TimelineStep[] }) {
  return (
    <ol className="relative space-y-0 border-l border-mist-200 pl-6">
      {steps.map((step, i) => (
        <li
          key={step.id}
          className="relative pb-8 last:pb-0 animate-fade-up"
          style={{ animationDelay: `${i * 80}ms` }}
        >
          <span
            className="absolute -left-[1.9rem] top-1 h-3 w-3 rounded-full border-2 border-navy-700 bg-white"
            aria-hidden
          />
          <p className="text-sm font-semibold text-ink-900">{step.label}</p>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-ink-700">
            {step.detail}
          </p>
        </li>
      ))}
    </ol>
  );
}

export default function EcosystemCharts({
  charts,
}: {
  charts: RestructuringChart[];
}) {
  if (!charts.length) return null;
  return (
    <div className="mt-12 space-y-12">
      <div>
        <h2 className="font-display text-2xl text-ink-900">Ecosystem maps</h2>
        <p className="mt-2 max-w-2xl text-sm text-ink-700">
          Visual flow of ownership, capital paths, and the transition sequence.
        </p>
      </div>
      {charts.map((chart) => (
        <section
          key={chart.id}
          className="border border-mist-200 bg-gradient-to-br from-white via-mist-50 to-mist-100/80 px-5 py-6 md:px-8 md:py-8"
        >
          <h3 className="font-display text-xl text-ink-900">{chart.title}</h3>
          {chart.caption && (
            <p className="mt-2 max-w-3xl text-sm text-ink-700">{chart.caption}</p>
          )}
          <div className="mt-6">
            {chart.layout === 'tree' && chart.nodes && (
              <TieredOrgChart nodes={chart.nodes} />
            )}
            {chart.layout === 'flows' && chart.flows && (
              <FlowsChart flows={chart.flows} />
            )}
            {chart.layout === 'timeline' && chart.steps && (
              <TimelineChart steps={chart.steps} />
            )}
          </div>
        </section>
      ))}
    </div>
  );
}
